/**
 * Branding Adapter
 *
 * Translates the `/admin-api/site/{siteId}/branding` wire shapes into domain
 * models, narrowing each slot map to the fixed vocabulary in `domainModels`.
 *
 * Deliberately does *not* validate colour values. A stored value that is not
 * `#RRGGBB` is carried through unchanged, because the editor has to show the
 * operator the value that needs fixing — nulling it here would present the slot
 * as unset and invite a save that silently cleared it.
 *
 * Requirements: 2.2, 2.5, 2.6
 */

import type { BrandAssetResponse, BrandRecordResponse, BrandingStateResponse } from './apiModels';
import {
  BRAND_ASSET_SLOTS,
  BRAND_COLOR_SLOTS,
  BRAND_TYPOGRAPHY_SLOTS,
  type BrandAsset,
  type BrandAssets,
  type BrandColors,
  type BrandRecord,
  type BrandTypography,
  type BrandingState,
} from './domainModels';

/**
 * Build an object over a fixed slot list.
 *
 * Keeps the slot vocabulary in one place — the const tuples in `domainModels` —
 * so adding a ninth colour cannot leave the adapter silently ignoring it, and a
 * slot the wire does not know about cannot leak into the domain.
 */
function mapSlots<K extends string, V>(
  slots: readonly K[],
  parse: (slot: K) => V
): { readonly [P in K]: V } {
  return slots.reduce(
    (acc, slot) => ({ ...acc, [slot]: parse(slot) }),
    {} as { [P in K]: V }
  );
}

function toBrandAsset(response: BrandAssetResponse): BrandAsset {
  return {
    url: response.url,
    // Renamed, not reinterpreted: these are the PNG's intrinsic pixels and
    // describe an aspect ratio, not a CSS size.
    widthPx: response.width,
    heightPx: response.height,
    alt: response.alt,
  };
}

function toBrandColors(source: Readonly<Record<string, string | null>>): BrandColors {
  return mapSlots(BRAND_COLOR_SLOTS, (slot) => source[slot] ?? null);
}

function toBrandTypography(
  source: Readonly<Record<string, string | null>>
): BrandTypography {
  return mapSlots(BRAND_TYPOGRAPHY_SLOTS, (slot) => source[slot] ?? null);
}

function toBrandAssets(
  source: Readonly<Record<string, BrandAssetResponse | null>>
): BrandAssets {
  return mapSlots(BRAND_ASSET_SLOTS, (slot) => {
    const asset = source[slot];
    return asset ? toBrandAsset(asset) : null;
  });
}

/** Convert one branding record — published or draft — to the domain model. */
export function toBrandRecord(response: BrandRecordResponse): BrandRecord {
  return {
    siteId: response.siteId,
    version: response.version,
    colors: toBrandColors(response.colors ?? {}),
    typography: toBrandTypography(response.typography ?? {}),
    assets: toBrandAssets(response.assets ?? {}),
    updatedAt: response.updatedAt,
    updatedBy: response.updatedBy,
  };
}

/**
 * Convert the read response to the domain model.
 *
 * Either record may be absent: no draft means nothing is pending, and no
 * published record with a draft present is a site mid-first-launch.
 */
export function toBrandingState(response: BrandingStateResponse): BrandingState {
  return {
    published: response.published ? toBrandRecord(response.published) : null,
    draft: response.draft ? toBrandRecord(response.draft) : null,
  };
}
